import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  ListItemText,
  FormHelperText,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";

import { useState } from "react";
import { useSuperAdminUsers } from "../../../context/SuperAdmin/UsersContext";
import { PostUserFields } from "../../../interfaces/SuperAdmin/Pages/iDataUser";
import { environment } from "../../../config";

const CreateUserModal = () => {
  const {
    isCreateModalOpen,
    toggleCreateModal,
    isCreating,
    roles,
    paises,
    userCreate,
  } = useSuperAdminUsers();

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [rolesError, setRolesError] = useState(false); // Estado para manejar el error de roles

  const { VERSION } = environment;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    register,
  } = useForm<PostUserFields>({});

  // Función para manejar el envío del formulario
  const handleSubmitForm = (data: PostUserFields) => {
    if (selectedRoles.length === 0) {
      setRolesError(true);
      return;
    }

    setRolesError(false);
    console.log(data);
    userCreate(data, selectedRoles);
    // Aquí puedes hacer la petición a la API
  };

  // Función para manejar la selección de roles
  const handleRoleChange = (roleId: string) => {
    setSelectedRoles(
      (prev) =>
        prev.includes(roleId)
          ? prev.filter((id) => id !== roleId) // Si ya está, lo quita
          : [...prev, roleId] // Si no está, lo agrega
    );
    setRolesError(false); // Quita el error cuando se selecciona un rol
  };

  const handleClose = () => {
    if (!isCreating) {
      reset();
      toggleCreateModal(false);
      setSelectedRoles([]);
      setRolesError(false);
    }
  };

  return (
    <Modal
      open={isCreateModalOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)", // Fondo negro con opacidad
      }}
    >
      <Box
        component="form"
        sx={{
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <Typography variant="h6" component="h2">
          Nuevo usuario
        </Typography>

        {/* Campo Nombre */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Nombres
          </Typography>
          <Controller
            name="nombres"
            control={control}
            rules={{ required: "El nombre es obligatorio" }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                error={!!errors.nombres}
                helperText={errors.nombres?.message}
                disabled={isCreating}
              />
            )}
          />
        </Box>

        {/* Campo Apellidos */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Apellidos
          </Typography>
          <Controller
            name="apellidos"
            control={control}
            rules={{ required: "El apellido es obligatorio" }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                error={!!errors.apellidos}
                helperText={errors.apellidos?.message}
                disabled={isCreating}
              />
            )}
          />
        </Box>

        {/* Campo Email */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Email
          </Typography>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "El email es obligatorio",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // NOSONAR
                message: "Formato de email inválido",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isCreating}
              />
            )}
          />
        </Box>

        {/* Campos Version */}
        <input type="hidden" {...register("version")} value={VERSION} />

        {/* Campo País */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            País
          </Typography>
          <Controller
            name="pais"
            control={control}
            rules={{ required: "Debe seleccionar un país" }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.pais}>
                <Select
                  {...field}
                  value={field.value || ""} // valor controlado
                  onChange={(e) => field.onChange(e.target.value)} // actualización
                  displayEmpty
                >
                  {paises.map((pais) => (
                    <MenuItem key={pais.id_pais} value={pais.codigo}>
                      {pais.nombre}
                    </MenuItem>
                  ))}
                </Select>
                {errors.pais && (
                  <FormHelperText>{errors.pais.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Campo Roles */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Roles
          </Typography>
          <FormControl fullWidth error={rolesError}>
            <Select
              value={selectedRoles}
              multiple
              onChange={() => {}} // No se usa porque el manejo de selección está en `onClick`
              renderValue={(selected) =>
                roles
                  .filter((role) => selected.includes(role.id_rol))
                  .map((role) => role.nombre)
                  .join(", ")
              }
            >
              {roles.map((role) => (
                <MenuItem
                  key={role.id_rol}
                  onClick={() => handleRoleChange(role.id_rol)}
                >
                  <Checkbox checked={selectedRoles.includes(role.id_rol)} />
                  <ListItemText primary={role.nombre} />
                </MenuItem>
              ))}
            </Select>
            {rolesError && (
              <FormHelperText>Debe seleccionar al menos un rol</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            variant="contained"
            sx={{ background: "#336B87" }}
            disabled={isCreating}
            type="submit"
          >
            Crear
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateUserModal;
